import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Paper, Typography, Grid, Chip, Link, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TableSortLabel, Tooltip, IconButton, Drawer, Divider, Stack,
  LinearProgress, FormControl, InputLabel, Select, MenuItem, Button,
  TextField, InputAdornment, Switch, FormControlLabel,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, Legend, ResponsiveContainer, LabelList,
} from 'recharts';
import { authGet, recheckPlatformInvestigationTicket } from '../../services/apiService';
import VitalSignsQueryLink from '../operational-excellence/VitalSignsQueryLink';

const ADO_BASE = 'https://tr-legal.visualstudio.com/HighQ%20Tech/_workitems/edit';

const CLOSED_STATES = ['Closed', 'Resolved', 'Done', 'Completed'];
const PRIORITY_LABELS = { 1: 'P1 – Critical', 2: 'P2 – High', 3: 'P3 – Medium', 4: 'P4 – Low' };
const PRIORITY_SHORT  = { 1: 'P1', 2: 'P2', 3: 'P3', 4: 'P4' };
const PRIORITY_COLORS = { 1: '#d32f2f', 2: '#f57c00', 3: '#1976d2', 4: '#388e3c' };
const STATE_COLORS = {
  New: '#1976d2', Active: '#f57c00', Resolved: '#388e3c',
  Closed: '#616161', Done: '#616161', 'On Hold': '#9c27b0',
};
const getStateColor = (s) => STATE_COLORS[s] || '#607d8b';


// Strip ADO email suffix: "First Last <email@domain>" → "First Last"
const shortName = (name) => {
  if (!name || name === 'Unassigned') return name || 'Unassigned';
  return name.replace(/\s*<[^>]+>/, '').trim();
};

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const daysSince = (d) => (d ? Math.round((Date.now() - new Date(d)) / 86400000) : null);
const pct = (n, total) => (total > 0 ? Math.round((n / total) * 100) : 0);

/** Teams whose Platform Investigation tickets this tab shows. Keep in sync with PI_PLATFORM_TEAMS in server/utils/platformInvestigationFilters.js. */
export const PLATFORM_INVESTIGATION_TEAMS = ['HQ-Platform-MI', 'HQ-Platform-SQ1'];

const PI_CARD_COLOR = '#0d47a1';

const CardMetric = ({ label, value, color }) => (
  <Box sx={{ textAlign: 'center', px: 0.5 }}>
    <Typography variant="h4" sx={{ fontWeight: 800, color, lineHeight: 1 }}>{value ?? '—'}</Typography>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, fontWeight: 500 }}>
      {label}
    </Typography>
  </Box>
);

const StatePill = ({ icon, text, bg, color }) => (
  <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.75, px: 1.25, py: 0.75, borderRadius: 2, bgcolor: bg, color }}>
    {icon}
    <Typography variant="caption" sx={{ fontWeight: 700 }}>{text}</Typography>
  </Box>
);

const PlatformOverviewCard = ({ open, total, p1, p2, newThisMonth, newToday, closedToday, byState }) => (
  <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1, px: 2.5, py: 1.5,
      background: `linear-gradient(135deg, ${PI_CARD_COLOR}ee 0%, ${PI_CARD_COLOR}bb 100%)`
    }}>
      <SearchIcon sx={{ color: '#fff' }} />
      <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 700, letterSpacing: 0.3 }}>
        Platform Investigation
      </Typography>
      <Box sx={{ ml: 'auto' }}><VitalSignsQueryLink tile="l3Platform" /></Box>
    </Box>
    <Box sx={{ p: 2.5, flexGrow: 1, position: 'relative' }}>
      <Box sx={{ position: 'absolute', top: 4, right: 4 }}>
        <Tooltip
          arrow
          placement="top"
          title={(
            <Box sx={{ fontSize: '0.75rem', lineHeight: 1.5 }}>
              <div><b>Open</b>: Platform Investigation tickets not in Closed, Resolved, Done or Completed. Matches the L3 Platform Services tile.</div>
              <div><b>Total</b>: all Platform Investigation tickets for the Platform teams, open and closed.</div>
              <div><b>P1 Critical / P2 High</b>: open tickets with that priority.</div>
              <div><b>New this month</b>: tickets created this calendar month, any state.</div>
              <div><b>New / closed today</b>: tickets created, or moved to a closed state, since midnight.</div>
              <div><b>Open by state</b>: open tickets grouped by current ADO state.</div>
            </Box>
          )}
        >
          <IconButton size="small"><InfoIcon fontSize="small" color="action" /></IconButton>
        </Tooltip>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-around', pb: 2 }}>
        <CardMetric label="Open" value={open} color={PI_CARD_COLOR} />
        <CardMetric label="Total" value={total} color="#555" />
      </Box>
      <Divider />
      <Box sx={{ display: 'flex', justifyContent: 'space-around', py: 2 }}>
        <CardMetric label="P1 Critical" value={p1} color="#c62828" />
        <CardMetric label="P2 High" value={p2} color="#e65100" />
        <CardMetric label="New this month" value={newThisMonth} color="#555" />
      </Box>
      <Divider />
      <Box sx={{ display: 'flex', gap: 1, py: 2 }}>
        <StatePill
          icon={<AddCircleOutlineIcon sx={{ fontSize: 14 }} />}
          text={`${newToday} new today`}
          bg="#eaf5ea"
          color="#2e7d32"
        />
        <StatePill
          icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
          text={`${closedToday} closed today`}
          bg="#e3f2fd"
          color="#0277bd"
        />
      </Box>
      <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 700, letterSpacing: 0.5, color: 'text.secondary' }}>
        OPEN BY STATE
      </Typography>
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
        {byState.map(([state, count]) => (
          <Box key={state} sx={{ display: 'flex', justifyContent: 'space-between', px: 1.5, py: 0.6, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption">{state}</Typography>
            <Typography variant="caption" sx={{ fontWeight: 700, color: PI_CARD_COLOR }}>{count}</Typography>
          </Box>
        ))}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1.5, py: 0.6, bgcolor: '#f5f5f5' }}>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>Open Total</Typography>
          <Typography variant="caption" sx={{ fontWeight: 700, color: PI_CARD_COLOR }}>{open}</Typography>
        </Box>
      </Box>
    </Box>
  </Paper>
);

// ── Detail drawer ─────────────────────────────────────────────────────────────
const DetailRow = ({ label, value, children }) => (
  <Box sx={{ display: 'flex', gap: 1, py: 0.5, alignItems: 'flex-start' }}>
    <Typography variant="caption" sx={{ width: 110, flexShrink: 0, color: 'text.secondary', fontWeight: 600, pt: 0.25 }}>
      {label}
    </Typography>
    {children ?? <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{value || '—'}</Typography>}
  </Box>
);

const ItemDrawer = ({ item, open, onClose, onRecheck, rechecking, recheckResult }) => {
  if (!item) return null;
  const isOpen = !CLOSED_STATES.includes(item.state);
  const age = isOpen ? daysSince(item.createdDate) : null;
  return (
    <Drawer anchor="right" open={open} onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 480 }, p: 0 } }}>
      <Box sx={{ px: 2.5, py: 2, bgcolor: '#1D4434', color: '#fff', display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" sx={{ opacity: 0.75 }}>#{item.id}</Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3, mt: 0.25 }}>{item.title}</Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: '#fff', mt: -0.5, flexShrink: 0 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box sx={{ px: 2.5, py: 1.25, bgcolor: '#f5f5f5', display: 'flex', alignItems: 'center', gap: 1 }}>
        <Tooltip title="Re-fetch this ticket from ADO to update its type. Use this if the ticket was converted to another work item type in ADO.">
          <span>
            <Button
              size="small"
              variant="outlined"
              color="warning"
              disabled={rechecking}
              onClick={() => onRecheck(item.id)}
              sx={{ fontSize: 11, py: 0.25, px: 1, minWidth: 0 }}
            >
              {rechecking ? <CircularProgress size={12} sx={{ mr: 0.5 }} /> : null}
              Re-check ADO type
            </Button>
          </span>
        </Tooltip>
        {recheckResult && (
          <Typography variant="caption"
            sx={{ color: recheckResult.typeChanged ? '#d32f2f' : '#388e3c', fontWeight: 600 }}>
            {recheckResult.typeChanged
              ? `Converted to: ${recheckResult.newType || 'unknown'}`
              : 'Still a PI ticket'}
          </Typography>
        )}
        <Link href={`${ADO_BASE}/${item.id}`} target="_blank" rel="noopener"
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 12, ml: 'auto' }}>
          Open in ADO <OpenInNewIcon sx={{ fontSize: 13 }} />
        </Link>
      </Box>
      <Divider />
      <Box sx={{ px: 2.5, py: 2, overflowY: 'auto' }}>
        <Stack spacing={0.25}>
          <DetailRow label="State">
            <Chip label={item.state} size="small"
              sx={{ fontSize: 11, height: 22, bgcolor: getStateColor(item.state), color: '#fff', fontWeight: 600 }} />
          </DetailRow>
          <DetailRow label="Priority">
            {item.priority
              ? <Chip label={PRIORITY_LABELS[item.priority] || `P${item.priority}`} size="small"
                  sx={{ fontSize: 11, height: 22, bgcolor: PRIORITY_COLORS[item.priority], color: '#fff', fontWeight: 600 }} />
              : '—'}
          </DetailRow>
          <DetailRow label="Assigned To" value={item.assignedTo} />
          <DetailRow label="Team" value={item.team} />
          <DetailRow label="Area Path" value={item.areaPath} />
          <DetailRow label="Iteration" value={item.iterationPath} />
          <DetailRow label="Created" value={fmtDate(item.createdDate)} />
          <DetailRow label="Last Updated" value={fmtDate(item.changedDate)} />
          <DetailRow label="Closed" value={fmtDate(item.closedDate)} />
          {age !== null && (
            <DetailRow label="Age (open)">
              <Typography variant="body2"
                sx={{ color: age > 90 ? '#d32f2f' : age > 30 ? '#f57c00' : 'inherit', fontWeight: age > 30 ? 700 : 400 }}>
                {age} days
              </Typography>
            </DetailRow>
          )}
          <DetailRow label="Tags" value={Array.isArray(item.tags) ? item.tags.join(', ') : item.tags} />
        </Stack>
        {item.description && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Description
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.75, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {item.description}
            </Typography>
          </>
        )}
      </Box>
    </Drawer>
  );
};

// ── Metric card ───────────────────────────────────────────────────────────────
const MetricCard = ({ title, value, subtitle, color }) => (
  <Paper elevation={0} sx={{ px: 1.5, py: 1, border: '1px solid', borderColor: 'divider', borderLeft: `3px solid ${color}`, height: '100%' }}>
    <Typography sx={{ fontWeight: 700, color, lineHeight: 1, mb: 0.25, fontSize: '1.05rem' }}>{value ?? '—'}</Typography>
    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', fontSize: 11 }}>{title}</Typography>
    {subtitle && <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>{subtitle}</Typography>}
  </Paper>
);

// ── Priority card ─────────────────────────────────────────────────────────────
const PriorityCard = ({ priority, count, total }) => {
  const color = PRIORITY_COLORS[priority] || '#607d8b';
  const label = PRIORITY_SHORT[priority] || `P${priority}`;
  const fullLabel = PRIORITY_LABELS[priority] || `P${priority}`;
  const p = pct(count, total);
  return (
    <Paper elevation={0} sx={{ px: 1.5, py: 1, border: '1px solid', borderColor: 'divider', borderLeft: `3px solid ${color}`, height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mb: 0.25 }}>
        <Chip label={label} size="small" sx={{ bgcolor: color, color: '#fff', fontWeight: 700, fontSize: 9, height: 16 }} />
        <Typography sx={{ fontWeight: 700, color, lineHeight: 1, fontSize: '1.05rem' }}>{count}</Typography>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: 10 }}>{fullLabel}</Typography>
      {total > 0 && <Typography variant="caption" sx={{ color, fontWeight: 600, fontSize: 10 }}>{p}% of open</Typography>}
    </Paper>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const PlatformInvestigationTeamTab = ({ teams, vitalSignsLayout = false }) => {
  const [items, setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);

  // List controls
  const [orderBy, setOrderBy]               = useState('createdDate');
  const [order, setOrder]                   = useState('desc');
  const [openOnly, setOpenOnly]             = useState(true);
  const [search, setSearch]                 = useState('');
  const [filterState, setFilterState]       = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterAssignee, setFilterAssignee] = useState('');
  const [startDate, setStartDate]           = useState('');
  const [endDate, setEndDate]               = useState('');

  // Detail drawer
  const [drawerItem, setDrawerItem] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [rechecking, setRechecking] = useState(false);
  const [recheckResult, setRecheckResult] = useState(null);

  // Monthly trend chart
  const [trendData, setTrendData]       = useState([]);
  const [trendLoading, setTrendLoading] = useState(true);

  const teamsKey = Array.isArray(teams) ? teams.join(',') : (teams || '');
  useEffect(() => {
    const doFetch = async () => {
      try {
        setLoading(true);
        const teamList = teamsKey ? teamsKey.split(',') : [];
        const results = await Promise.all(
          teamList.map(t => authGet(`/api/platform-investigation?team=${encodeURIComponent(t)}&pageSize=500`, {}, 'json', { useCache: false }))
        );
        for (const r of results) {
          if (!r.success) throw new Error(r.message || 'Failed to load');
        }
        const seen = new Set();
        const merged = [];
        for (const r of results) {
          for (const item of (r.data || [])) {
            if (!seen.has(item.id)) { seen.add(item.id); merged.push(item); }
          }
        }
        setItems(merged);
        setError(null);
      } catch (e) {
        setError(e.message || 'Failed to load');
      } finally {
        setLoading(false);
      }
    };
    doFetch();
  }, [teamsKey]);

  useEffect(() => {
    authGet('/api/platform-investigation/trends')
      .then(r => { if (r.success) setTrendData(r.data || []); })
      .catch(() => {})
      .finally(() => setTrendLoading(false));
  }, []);

  // ── Derived metrics ──────────────────────────────────────────────────────────
  const total     = items.length;
  const completed = useMemo(() => items.filter(t => CLOSED_STATES.includes(t.state)), [items]);
  const pending   = useMemo(() => items.filter(t => !CLOSED_STATES.includes(t.state)), [items]);

  const completedCount = completed.length;
  const pendingCount   = pending.length;
  const completedPct   = pct(completedCount, total);
  const pendingPct     = pct(pendingCount, total);

  // Priority breakdown (open/pending only)
  const openByPriority = useMemo(() => {
    const counts = { 1: 0, 2: 0, 3: 0, 4: 0 };
    pending.forEach(t => { if ([1, 2, 3, 4].includes(t.priority)) counts[t.priority]++; });
    return counts;
  }, [pending]);

  // Avg tickets created per week (last 12 months)
  const avgPerWeek = useMemo(() => {
    const cutoff = new Date();
    cutoff.setFullYear(cutoff.getFullYear() - 1);
    const count = items.filter(t => t.createdDate && new Date(t.createdDate) >= cutoff).length;
    return count > 0 ? (count / 52).toFixed(1) : '—';
  }, [items]);

  // Avg days to close — all time
  const avgClosureDays = useMemo(() => {
    const closed = items.filter(t => CLOSED_STATES.includes(t.state) && t.createdDate);
    if (!closed.length) return null;
    const sum = closed.reduce((acc, t) => {
      const cd = new Date(t.closedDate || t.changedDate);
      return acc + Math.max(0, Math.round((cd - new Date(t.createdDate)) / 86400000));
    }, 0);
    return Math.round(sum / closed.length);
  }, [items]);

  // Avg days to close — last 90 days (resolution time)
  const avgResolutionDays = useMemo(() => {
    const cutoff = new Date(Date.now() - 90 * 86400000);
    const recent = items.filter(t => {
      if (!CLOSED_STATES.includes(t.state) || !t.createdDate) return false;
      return new Date(t.closedDate || t.changedDate) >= cutoff;
    });
    if (!recent.length) return null;
    const sum = recent.reduce((acc, t) => {
      const cd = new Date(t.closedDate || t.changedDate);
      return acc + Math.max(0, Math.round((cd - new Date(t.createdDate)) / 86400000));
    }, 0);
    return Math.round(sum / recent.length);
  }, [items]);

  // Metrics for the overview card (same ticket set as the list below)
  const overviewCard = useMemo(() => {
    const now = new Date();
    const sameDay = (d) => d && new Date(d).toDateString() === now.toDateString();
    const sameMonth = (d) => {
      if (!d) return false;
      const date = new Date(d);
      return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
    };
    const byStateMap = {};
    pending.forEach((t) => {
      const state = t.state || 'Unknown';
      byStateMap[state] = (byStateMap[state] || 0) + 1;
    });
    return {
      open: pendingCount,
      total,
      p1: openByPriority[1],
      p2: openByPriority[2],
      newThisMonth: items.filter((t) => sameMonth(t.createdDate)).length,
      newToday: items.filter((t) => sameDay(t.createdDate)).length,
      closedToday: completed.filter((t) => sameDay(t.closedDate || t.changedDate)).length,
      byState: Object.entries(byStateMap).sort((a, b) => b[1] - a[1])
    };
  }, [items, pending, completed, pendingCount, total, openByPriority]);

  // Filter option lists derived from full dataset
  const stateOptions    = useMemo(() => [...new Set(items.map(t => t.state).filter(Boolean))].sort(), [items]);
  const assigneeOptions = useMemo(() => [...new Set(items.map(t => t.assignedTo).filter(Boolean))].sort(), [items]);

  // Sorted + filtered list
  const displayed = useMemo(() => {
    let rows = [...items];
    if (openOnly)       rows = rows.filter(t => !CLOSED_STATES.includes(t.state));
    if (filterState)    rows = rows.filter(t => t.state === filterState);
    if (filterPriority) rows = rows.filter(t => t.priority === Number(filterPriority));
    if (filterAssignee) rows = rows.filter(t => (t.assignedTo || 'Unassigned') === filterAssignee);
    if (startDate)      rows = rows.filter(t => t.createdDate && new Date(t.createdDate) >= new Date(startDate));
    if (endDate)        rows = rows.filter(t => t.createdDate && new Date(t.createdDate) <= new Date(endDate + 'T23:59:59'));
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter(t =>
        String(t.id || '').includes(q) ||
        (t.title || '').toLowerCase().includes(q) ||
        (t.assignedTo || '').toLowerCase().includes(q)
      );
    }
    return rows.sort((a, b) => {
      let va = a[orderBy], vb = b[orderBy];
      if (['createdDate', 'closedDate', 'changedDate'].includes(orderBy)) {
        va = va ? new Date(va).getTime() : 0;
        vb = vb ? new Date(vb).getTime() : 0;
      } else if (orderBy === 'priority') {
        va = va ?? 99; vb = vb ?? 99;
      } else {
        va = (va || '').toString().toLowerCase();
        vb = (vb || '').toString().toLowerCase();
      }
      if (va < vb) return order === 'asc' ? -1 : 1;
      if (va > vb) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [items, openOnly, filterState, filterPriority, filterAssignee, startDate, endDate, search, orderBy, order]);

  const handleSort = (col) => {
    if (orderBy === col) setOrder(o => o === 'asc' ? 'desc' : 'asc');
    else { setOrderBy(col); setOrder('desc'); }
  };

  const handleRecheck = async (workItemId) => {
    setRechecking(true);
    setRecheckResult(null);
    try {
      const r = await recheckPlatformInvestigationTicket(workItemId);
      if (r.success) {
        setRecheckResult({ typeChanged: r.typeChanged, newType: r.newType });
        if (r.typeChanged) {
          // Remove the converted ticket from the local list and close the drawer
          setItems(prev => prev.filter(t => t.id !== workItemId));
          setTimeout(() => { setDrawerOpen(false); setRecheckResult(null); }, 1500);
        }
      }
    } catch (e) {
      setRecheckResult({ typeChanged: false, newType: null, error: true });
    } finally {
      setRechecking(false);
    }
  };

  const clearAllFilters = () => {
    setOpenOnly(true); setSearch(''); setFilterState('');
    setFilterPriority(''); setFilterAssignee(''); setStartDate(''); setEndDate('');
  };

  const activeFilterCount = [
    !openOnly, search, filterState, filterPriority, filterAssignee, startDate, endDate
  ].filter(Boolean).length;

  const SortCell = ({ col, label }) => (
    <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', bgcolor: '#f5f5f5', py: 1 }}>
      <TableSortLabel active={orderBy === col} direction={orderBy === col ? order : 'asc'} onClick={() => handleSort(col)}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }
  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      {/* ── Summary strip ── */}
      {!vitalSignsLayout && (
      <Paper elevation={0} sx={{ p: 1.5, mb: 2, border: '1px solid #e0e7ef', borderRadius: '10px', bgcolor: '#fafbfc' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {[
            { label: 'Total',          value: total,                                  color: '#1D4434' },
            { label: 'Open',           value: pendingCount,   sub: `${pendingPct}%`,  color: '#f57c00' },
            { label: 'Closed',         value: completedCount, sub: `${completedPct}%`,color: '#388e3c' },
            { label: 'P1 Critical',    value: openByPriority[1],                      color: '#d32f2f' },
            { label: 'P2 High',        value: openByPriority[2],                      color: '#f57c00' },
            { label: 'P3 Medium',      value: openByPriority[3],                      color: '#1976d2' },
            { label: 'P4 Low',         value: openByPriority[4],                      color: '#388e3c' },
            { label: 'Avg / Week',     value: avgPerWeek,                             color: '#7c3aed' },
            { label: 'Avg Closure',    value: avgClosureDays   != null ? `${avgClosureDays}d`    : '—', sub: 'all time',  color: '#0891b2' },
            { label: 'Avg Resolution', value: avgResolutionDays != null ? `${avgResolutionDays}d` : '—', sub: 'last 90d', color: '#0891b2' },
          ].map(({ label, value, sub, color }) => (
            <Box key={label} sx={{
              px: 1.5, py: 1, bgcolor: '#fff', borderRadius: 1,
              border: '1px solid #e5eaf0', borderLeft: `3px solid ${color}`,
              minWidth: 72, flex: '0 0 auto',
            }}>
              <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color, lineHeight: 1 }}>{value ?? '—'}</Typography>
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', fontSize: 10, color: 'text.secondary', mt: 0.25 }}>{label}</Typography>
              {sub && <Typography variant="caption" sx={{ fontSize: 9, color: 'text.secondary' }}>{sub}</Typography>}
            </Box>
          ))}
        </Box>
      </Paper>
      )}

      {/* ── Monthly Created vs Resolved trend ── */}
      <Grid container spacing={2} sx={vitalSignsLayout ? { mb: 3 } : undefined}>
      {vitalSignsLayout && (
        <Grid item xs={12} md={4}>
          <PlatformOverviewCard {...overviewCard} />
        </Grid>
      )}
      <Grid item xs={12} md={vitalSignsLayout ? 8 : 12}>
      <Paper
        elevation={2}
        sx={{
          boxSizing: 'border-box', p: 2, mb: vitalSignsLayout ? 0 : 3,
          ...(vitalSignsLayout ? { height: '100%', display: 'flex', flexDirection: 'column' } : {})
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
          Monthly Created vs Resolved — Last 12 Months
        </Typography>
        {trendLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
        ) : trendData.length === 0 ? (
          <Typography color="text.secondary" variant="body2">No trend data available.</Typography>
        ) : (
          <Box sx={vitalSignsLayout ? { position: 'relative', flex: '1 1 0', minHeight: 240 } : undefined}>
          <Box sx={vitalSignsLayout ? { position: 'absolute', inset: 0 } : undefined}>
          <ResponsiveContainer width="100%" height={vitalSignsLayout ? '100%' : 260}>
            <ComposedChart
              data={trendData.map(d => ({ month: d.month, Created: d.created, Resolved: d.closed, Backlog: d.backlog }))}
              margin={{ top: 20, right: 20, left: 0, bottom: 4 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={30} />
              <RechartsTooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Created" fill="#1976d2" radius={[3, 3, 0, 0]}>
                <LabelList dataKey="Created" position="top" style={{ fontSize: 10, fontWeight: 600, fill: '#1565c0' }} />
              </Bar>
              <Bar dataKey="Resolved" fill="#388e3c" radius={[3, 3, 0, 0]}>
                <LabelList dataKey="Resolved" position="top" style={{ fontSize: 10, fontWeight: 600, fill: '#2e7d32' }} />
              </Bar>
              <Line type="monotone" dataKey="Backlog" stroke="#e65100" strokeWidth={2} dot={{ r: 4 }} name="Backlog">
                <LabelList dataKey="Backlog" position="top" style={{ fontSize: 10, fontWeight: 600, fill: '#e65100' }} />
              </Line>
            </ComposedChart>
          </ResponsiveContainer>
          </Box>
          </Box>
        )}
      </Paper>
      </Grid>
      </Grid>

      {/* ── Overall progress ── */}
      {!vitalSignsLayout && (
      <Paper elevation={1} sx={{ px: 2, py: 1, mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>Completion Progress</Typography>
          <Typography variant="caption" sx={{ fontWeight: 700, color: '#388e3c', ml: 'auto' }}>
            {completedCount} / {total} · {completedPct}%
          </Typography>
          <Typography variant="caption" color="text.secondary">{pendingCount} pending</Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={completedPct}
          sx={{
            height: 8,
            borderRadius: 4,
            bgcolor: '#e8f5e9',
            '& .MuiLinearProgress-bar': { bgcolor: '#388e3c', borderRadius: 4 }
          }}
        />
      </Paper>
      )}

      {/* ── Ticket list ── */}
      <Paper elevation={2} sx={{ p: 2 }}>
        {/* Header row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
          <FilterListIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap', minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              All Tickets
            </Typography>
            {vitalSignsLayout && (
              // Import WIQL: syncWorkItemsByType in adoFetchService.js; list rules: platformInvestigationFilters.js
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                ADO query: Project = HighQ Tech AND Work Item Type = Platform Investigation AND State &lt;&gt; Removed
                {' '}| List shows: Team = {teamsKey.split(',').filter(Boolean).join(' or ')}
                {' '}(Open only hides Closed, Resolved, Done and Completed)
              </Typography>
            )}
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={openOnly}
                onChange={e => { setOpenOnly(e.target.checked); if (e.target.checked) setFilterState(''); }}
                size="small"
                color="success"
              />
            }
            label={<Typography variant="caption" sx={{ fontWeight: 600 }}>Open only</Typography>}
            sx={{ m: 0 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {displayed.length}{activeFilterCount > 0 ? ` of ${total}` : ''} ticket{displayed.length !== 1 ? 's' : ''}
            {activeFilterCount > 0 ? ` · ${activeFilterCount} filter${activeFilterCount > 1 ? 's' : ''}` : ''}
          </Typography>
          {activeFilterCount > 0 && (
            <Button size="small" variant="outlined" color="inherit" sx={{ fontSize: 11, py: 0.25, px: 1 }}
              onClick={clearAllFilters}>
              Clear all
            </Button>
          )}
        </Box>

        {/* Filter controls */}
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              size="small" fullWidth
              placeholder="Search ID · title · assignee…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: 'text.secondary' }} /></InputAdornment>,
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch('')}><CloseIcon sx={{ fontSize: 14 }} /></IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <FormControl size="small" fullWidth disabled={openOnly}>
              <InputLabel>State</InputLabel>
              <Select value={filterState} label="State" onChange={e => setFilterState(e.target.value)}>
                <MenuItem value=""><em>All</em></MenuItem>
                {stateOptions.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <FormControl size="small" fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select value={filterPriority} label="Priority" onChange={e => setFilterPriority(e.target.value)}>
                <MenuItem value=""><em>All</em></MenuItem>
                <MenuItem value="1">P1 – Critical</MenuItem>
                <MenuItem value="2">P2 – High</MenuItem>
                <MenuItem value="3">P3 – Medium</MenuItem>
                <MenuItem value="4">P4 – Low</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <FormControl size="small" fullWidth>
              <InputLabel>Assignee</InputLabel>
              <Select value={filterAssignee} label="Assignee" onChange={e => setFilterAssignee(e.target.value)}>
                <MenuItem value=""><em>All</em></MenuItem>
                {assigneeOptions.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={1}>
            <TextField size="small" fullWidth label="From" type="date"
              InputLabelProps={{ shrink: true }} value={startDate}
              onChange={e => setStartDate(e.target.value)} />
          </Grid>
          <Grid item xs={6} sm={3} md={1}>
            <TextField size="small" fullWidth label="To" type="date"
              InputLabelProps={{ shrink: true }} value={endDate}
              onChange={e => setEndDate(e.target.value)} />
          </Grid>
        </Grid>

        {filterAssignee && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, px: 1, py: 0.75,
            bgcolor: '#f3f4f6', borderRadius: 1, border: '1px solid #e5e7eb' }}>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              Showing tickets for: {shortName(filterAssignee)}
            </Typography>
            <IconButton size="small" sx={{ ml: 'auto', p: 0.25 }} onClick={() => setFilterAssignee('')}>
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        )}

        {displayed.length === 0 ? (
          <Typography color="text.secondary" variant="body2" sx={{ textAlign: 'center', py: 3 }}>
            No tickets match the selected filters.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small" sx={{ minWidth: 700 }}>
              <TableHead>
                <TableRow>
                  <SortCell col="id" label="ID" />
                  <SortCell col="title" label="Title" />
                  <SortCell col="state" label="State" />
                  <SortCell col="priority" label="Priority" />
                  <SortCell col="assignedTo" label="Assigned To" />
                  <SortCell col="createdDate" label="Created" />
                  <SortCell col="changedDate" label="Updated" />
                  <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f5f5', whiteSpace: 'nowrap', py: 1 }}>Age</TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f5f5', py: 1 }}>Progress</TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f5f5', py: 1, whiteSpace: 'nowrap' }}>Linked (PBI / Bug)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayed.map((item) => {
                  const isClosed = CLOSED_STATES.includes(item.state);
                  const age      = !isClosed ? daysSince(item.createdDate) : null;
                  const devName  = item.assignedTo || 'Unassigned';
                  return (
                    <TableRow
                      key={item.id}
                      hover
                      sx={{
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                      onClick={() => { setDrawerItem(item); setDrawerOpen(true); setRecheckResult(null); }}
                    >
                      <TableCell sx={{ whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                        <Link href={`${ADO_BASE}/${item.id}`} target="_blank" rel="noopener"
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#1976d2', textDecoration: 'none', fontSize: 12, '&:hover': { textDecoration: 'underline' } }}>
                          {item.id}<OpenInNewIcon sx={{ fontSize: 11 }} />
                        </Link>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 300 }}>
                        <Tooltip title={item.title} placement="top-start">
                          <Typography variant="body2"
                            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 280, fontSize: 13 }}>
                            {item.title}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip label={item.state || '—'} size="small"
                          sx={{ fontSize: 10, height: 20, bgcolor: getStateColor(item.state), color: '#fff', fontWeight: 600 }} />
                      </TableCell>
                      <TableCell>
                        {item.priority
                          ? <Chip label={PRIORITY_SHORT[item.priority] || `P${item.priority}`} size="small"
                              sx={{ fontSize: 10, height: 20, bgcolor: PRIORITY_COLORS[item.priority] || '#607d8b', color: '#fff', fontWeight: 600 }} />
                          : <Typography variant="caption" color="text.secondary">—</Typography>}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 12 }}>
                        {shortName(devName)}
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>{fmtDate(item.createdDate)}</TableCell>
                      <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>{fmtDate(item.changedDate)}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>
                        {age !== null
                          ? <Typography variant="caption"
                              sx={{ color: age > 90 ? '#d32f2f' : age > 30 ? '#f57c00' : 'text.secondary', fontWeight: age > 30 ? 600 : 400 }}>
                              {age}d
                            </Typography>
                          : '—'}
                      </TableCell>
                      <TableCell sx={{ minWidth: 80 }}>
                        <Tooltip title={isClosed ? 'Completed' : item.state || 'Pending'}>
                          <Box>
                            <LinearProgress
                              variant="determinate"
                              value={isClosed ? 100 : 0}
                              sx={{
                                height: 6,
                                borderRadius: 3,
                                bgcolor: '#f5f5f5',
                                '& .MuiLinearProgress-bar': {
                                  bgcolor: isClosed ? '#388e3c' : item.state === 'Active' ? '#f57c00' : '#9e9e9e',
                                  borderRadius: 3
                                }
                              }}
                            />
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ minWidth: 200 }} onClick={e => e.stopPropagation()}>
                        {(!item.linkedItems || item.linkedItems.length === 0) ? (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        ) : (
                          <Stack spacing={0.75}>
                            {item.linkedItems.map(l => {
                              const lClosed = CLOSED_STATES.includes(l.state);
                              const lPct = lClosed ? 100 : (l.completionPercentage || 0);
                              const isBug = l.workItemType === 'Bug';
                              return (
                                <Box key={l.id}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'nowrap' }}>
                                    <Chip label={isBug ? 'Bug' : 'PBI'} size="small"
                                      sx={{ height: 14, fontSize: 9, bgcolor: isBug ? '#d32f2f' : '#1565c0', color: '#fff', fontWeight: 700 }} />
                                    <Link href={`${ADO_BASE}/${l.id}`} target="_blank" rel="noopener"
                                      sx={{ fontSize: 11, color: '#1976d2', display: 'flex', alignItems: 'center', gap: 0.25, '&:hover': { textDecoration: 'underline' } }}>
                                      {l.id}<OpenInNewIcon sx={{ fontSize: 10 }} />
                                    </Link>
                                    {l.fixVersion && (
                                      <Tooltip title={l.fixVersion} arrow>
                                        <Chip label={l.fixVersion} size="small"
                                          sx={{ height: 14, fontSize: 9, maxWidth: 80, bgcolor: '#e8f5e9', color: '#2e7d32',
                                            '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis', px: 0.75 } }} />
                                      </Tooltip>
                                    )}
                                  </Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                                    <LinearProgress variant="determinate" value={lPct}
                                      sx={{
                                        width: 56, height: 4, borderRadius: 2, flexShrink: 0,
                                        bgcolor: '#f0f0f0',
                                        '& .MuiLinearProgress-bar': {
                                          bgcolor: lClosed ? '#388e3c' : lPct > 0 ? '#1976d2' : '#bdbdbd',
                                          borderRadius: 2
                                        }
                                      }}
                                    />
                                    <Typography variant="caption" sx={{ fontSize: 9, color: 'text.secondary', width: 24 }}>{lPct}%</Typography>
                                    <Chip label={l.state || '?'} size="small"
                                      sx={{ height: 14, fontSize: 9, bgcolor: getStateColor(l.state), color: '#fff', fontWeight: 600 }} />
                                  </Box>
                                </Box>
                              );
                            })}
                          </Stack>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <ItemDrawer
        item={drawerItem}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onRecheck={handleRecheck}
        rechecking={rechecking}
        recheckResult={recheckResult}
      />
    </Box>
  );
};

export default PlatformInvestigationTeamTab;
